import React, { useState, useContext, useEffect, useCallback, useRef } from 'react'
import {
  AddButton, AddButtonImg, AddButtonText, AddProductContainer, Button, Card,
  CollapsibleHeader, FirstText, FormGrid, FormGroup, InfoLink, InfoName, InfoValue, InputText,
  Label, MoreInformation, SecondText, Select, Separator, ShortLabel, ShortLabelText,
  SeparatorImg, Summary, SummaryItem, SummaryLabel, SummaryValue, KitSummaryLabel, Title,
  ActionButtonsRow, InfoPopoverWrapper, OptionAddRow, OptionSelectRow,
  ProductFieldsRow, KitSummaryRow, KitTrashImg, ActionIconImg,
} from './style'
import { ChevronDown, ChevronUp, Info, Plus } from 'lucide-react'
import plus from '../../../../assets/fashion/TableFashionHeader/plus.svg'
import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg'
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg'
import editPen from '../../../../assets/fashion/TableFashionHeader/edit-pen.svg'
import { useOutletContext, useParams } from 'react-router-dom'
import axios from '../../../../config/axios'
import { ActionsDiv, ProductDiv, ProductShort } from '../AddProductForm/style'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import AttachmentManager, { ExistingAttachment } from '../../../../components/AttachmentManager'
import { downloadRemoteFile } from '../../../../utils/downloadRemoteFile'
import VendorSelect from '../SelectItem/VendorSelect'
import CompanySelect from '../SelectItem/CompanySelect'
import AddProductForm from '../AddProductForm'
import InfiniteSelect from '../../../../components/InfiniteSelect'

const ORDERS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`
const UPLOAD_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/order/upload-attachment`
const FILE_SIZE_LIMIT = 200 * 1024 * 1024

interface FashionOrderProductItemInput { colorId: number; size: string; quantity: number }
type SizeQuantities = { [size: string]: number }

interface ProductApiData {
  fashionProductId?: number
  discount: number | null
  totalItems: number
  subtotal: number
  items: FashionOrderProductItemInput[]
  fashionOrderId?: number
  fashionProduct?: {
    id: number; name: string; price: number; size: string[]
    group: { id: number; name?: string; family?: { id: number; name?: string; collectionId?: number } }
  }
  colorCount?: number
  colors?: string[]
  family?: { id: number; name?: string; familyId?: number }
  group?: { id: number; name?: string }
  name?: string
  price?: number
  selectedColors?: number[]
  discountValue?: number
  sizeQuantities?: SizeQuantities
  kitIndex?: number | null
  isKitIntent?: boolean
}

interface CompanyItemType { id: number; companyName: string }
interface UserItemType { id: number; name: string }

type FormContent = {
  handleSave?: React.MutableRefObject<() => void>
  handleEditSave?: React.MutableRefObject<() => Promise<void>>
  setIsSaving?: (val: boolean) => void
}

const cleanCurrency = (value: any): number => {
  const num = Number(String(value || '').replace(/[^0-9,-]+/g, '').replace(',', '.'))
  return isNaN(num) ? 0 : num
}

const centsDigitsToReais = (value: unknown): number => {
  const digits = String(value ?? '').replace(/\D/g, '')
  if (!digits) return 0
  return Number(digits) / 100
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value)

const formatCentDigitsForInput = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const digits = String(value).replace(/\D/g, '')
  if (!digits) return ''
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(digits) / 100)
}

const formatDate = (iso: string | null | undefined): string =>
  iso ? iso.substring(0, 10) : ''

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
}

const getSafeValue = (value: any): string => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'object') return ('name' in value ? value.name : null) || '-'
  return String(value) || '-'
}

const countColors = (product: ProductApiData): number => {
  if (product.colorCount && product.colorCount > 0) return product.colorCount
  if (product.items?.length) return new Set(product.items.map((i) => i.colorId)).size
  return product.colors?.length ?? 0
}

const uploadFileToGeneralFiles = async (file: File): Promise<number> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('title', file.name)
  const { data } = await axios.post<{ id: number }>(`${UPLOAD_URL}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data.id
}

const RegisterFashionOrder: React.FC = () => {
  const { orderId } = useParams<{ orderId?: string }>()
  const isEditing = !!orderId

  const outletContext = useOutletContext<FormContent>()
  const { setFeedback } = useContext(FeedbackContext)
  const formTopRef = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(isEditing)
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)
  const [isInformationOpen, setIsInformationOpen] = useState(false)
  const [isClientInformationOpen, setIsClientInformationOpen] = useState(false)
  const [isSellerInfoOpen, setIsSellerInfoOpen] = useState(false)

  const [addedProducts, setAddedProducts] = useState<ProductApiData[]>([])
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(null)
  const [productToEdit, setProductToEdit] = useState<ProductApiData | null>(null)
  const [openProductIndex, setOpenProductIndex] = useState<number | null>(null)

  const [kitCount, setKitCount] = useState(0)
  const [showAddProductForSection, setShowAddProductForSection] = useState<'standalone' | number | null>('standalone')

  const [shippingOptions, setShippingOptions] = useState(['CIF', 'FOB', 'Cortesia'])
  const [paymentOptions, setPaymentOptions] = useState(['30 dias', '30/60 dias', '30/60/90 dias', 'A vista'])
  const [newShippingType, setNewShippingType] = useState('')
  const [newPaymentCondition, setNewPaymentCondition] = useState('')
  const [isAddingPayment, setIsAddingPayment] = useState(false)
  const [isAddingShipping, setIsAddingShipping] = useState(false)

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachment[]>([])
  const [existingFileIds, setExistingFileIds] = useState<number[]>([])
  const [removedFileIds, setRemovedFileIds] = useState<number[]>([])
  const [isKitMode, setIsKitMode] = useState(false);

  const [formState, setFormState] = useState({
    supplier: { id: 0, companyName: '' } as CompanyItemType,
    seller: { id: 0, name: '' } as UserItemType,
    company: { id: 0, companyName: '' } as CompanyItemType,
    orderType: '',
    paymentCondition: null as { id: number; name: string } | null,
    shippingType: null as { id: number; name: string } | null,
    trackingCode: '',
    finalDateOfDelivery: '', startDateOfDelivery: '', extraInformation: '',
    quantityItems: '0', quantityPieces: '0', totalCost: 'R$ 0,00',
    deliveryTime: '', shippingCost: '', maximumBillingAmount: '',
  })

  const setField = (field: string, value: any) =>
    setFormState((prev) => ({ ...prev, [field]: value }))

  const normalizeProductData = useCallback((product: any): ProductApiData => {
    const sizeQuantities: SizeQuantities = {}
      ; (product.items || []).forEach((item: FashionOrderProductItemInput) => {
        if (item.size && item.quantity !== undefined)
          sizeQuantities[item.size] = (sizeQuantities[item.size] || 0) + Number(item.quantity)
      })
    const totalItems = Object.values(sizeQuantities).reduce((sum, qty) => sum + qty, 0)
    const subtotal = product.subtotal ?? totalItems * (product.fashionProduct?.price || 0)
    const colorCount = product.colorCount || new Set((product.items || []).map((i: any) => i.colorId)).size

    return {
      ...product,
      sizeQuantities,
      totalItems,
      subtotal,
      colorCount,
      items: product.items || [],
      kitIndex: product.kitIndex !== undefined ? product.kitIndex : null,
    }
  }, [])

  useEffect(() => {
    if (!isEditing) return
    const loadOrder = async () => {
      setLoading(true)
      try {
        const { data } = await axios.get(`${ORDERS_URL}/order/${orderId}`)

        let sellerName = data.sellerDetails?.name || data.seller || '';
        if (!sellerName && data.sellerId) {
          try {
            const sellerRes = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/${data.sellerId}`);
            sellerName = sellerRes.data?.name || sellerName;
          } catch (e) {
            console.warn(`Não foi possível buscar vendedor ${data.sellerId} durante edição:`, e);
          }
        }

        setFormState({
          supplier: { id: Number(data.supplierId), companyName: data.supplierDetails?.name || data.supplier || '' },
          seller: { id: data.sellerId || 0, name: sellerName || '' },
          company: { id: Number(data.clientId), companyName: data.clientDetails?.name || data.client || '' },
          orderType: data.orderType || '',
          paymentCondition: typeof data.paymentCondition === 'object' && data.paymentCondition !== null
            ? data.paymentCondition
            : (data.paymentTerm ? { id: 0, name: data.paymentTerm } : null),
          shippingType: typeof data.shippingType === 'object' && data.shippingType !== null
            ? data.shippingType
            : (data.shippingType ? { id: 0, name: data.shippingType } : null),
          trackingCode: data.trackingCode || '',
          finalDateOfDelivery: formatDate(data.finalDateOfDelivery),
          startDateOfDelivery: formatDate(data.startDateOfDelivery || data.orderDate),
          extraInformation: data.extraInfo || data.extraInformation || '',
          quantityItems: data.quantityItems || '0',
          quantityPieces: data.quantityPieces || '0',
          totalCost: `R$ ${Number(data.totalCost || 0).toFixed(2).replace('.', ',')}`,
          deliveryTime: data.deliveryTime || '',
          shippingCost:
            data.shippingValue != null && data.shippingValue !== ''
              ? String(Math.round(Number(data.shippingValue) * 100))
              : data.shippingCost != null && data.shippingCost !== ''
                ? String(Math.round(Number(data.shippingCost) * 100))
                : '',
          maximumBillingAmount: data.maximumBillingAmount || '',
        })

        const attachments: ExistingAttachment[] = (data.attachments || []).map((a: any) => ({
          id: a.id,
          originalName: a.originalName || a.title || a.fileName || 'Arquivo',
          filePath: a.fileUrl || a.filePath,
          fileSize: a.fileSize,
          mimeType: a.mimeType,
        }))
        setExistingAttachments(attachments)
        setExistingFileIds(attachments.map((a) => a.id))
        setRemovedFileIds([])

        const products = (data.items || [])
          .filter((item: any) => item.itemType === 'PRODUCT')
          .map((item: any) => {
            let extra: any = {}
            try { extra = item.extraInfo ? JSON.parse(item.extraInfo) : {} } catch { /* ignore */ }

            const P = item.Product as { Family?: { id?: number; name?: string }; family?: { id?: number; name?: string } } | undefined
            const famRel = P?.Family || P?.family
            const famId = Number(extra.familyId ?? famRel?.id ?? 0)
            const famName = String(extra.familyName || famRel?.name || '').trim()

            return normalizeProductData({
              fashionProductId: item.productId,
              fashionProduct: {
                id: item.productId,
                name: extra.fashionProductName || item.Product?.name || '',
                price: extra.fashionProductPrice || item.Product?.price || 0,
                group: {
                  id: extra.groupId || item.Product?.group?.id,
                  name: extra.groupName || item.Product?.group?.name || '',
                  family: {
                    id: famId,
                    name: famName,
                    collectionId: extra.collectionId,
                  },
                },
              },
              family: famName ? { id: famId, name: famName } : undefined,
              discount: item.descount || 0,
              totalItems: item.quantity || 0,
              subtotal: item.priceKg || 0,
              items: extra.fashionItems || [],
              colorCount: extra.colorCount || 0,
              colors: extra.colors || [],
              selectedColors: extra.selectedColors || [],
              kitIndex: item.kitIndex !== undefined ? item.kitIndex : null,
            })
          })

        setAddedProducts(products)

        const maxKitIndex = products.reduce((max: number, p: ProductApiData) => {
          if (p.kitIndex !== null && p.kitIndex !== undefined && p.kitIndex > max) return p.kitIndex
          return max
        }, -1)
        setKitCount(maxKitIndex >= 0 ? maxKitIndex + 1 : 0)

      } catch (error) {
        console.error('Erro ao buscar o pedido:', error)
      }
      setLoading(false)
    }
    loadOrder()
  }, [orderId, isEditing, normalizeProductData])

  useEffect(() => {
    const totalPieces = addedProducts.reduce((sum, p) => {
      const pieces = p.sizeQuantities
        ? Object.values(p.sizeQuantities).reduce((s, q) => s + q, 0)
        : p.totalItems || 0
      return sum + pieces
    }, 0)
    const totalCost = addedProducts.reduce((sum, p) => sum + (p.subtotal || 0), 0)

    setFormState((prev) => ({
      ...prev,
      quantityItems: String(addedProducts.length),
      quantityPieces: String(totalPieces),
      totalCost: formatCurrency(totalCost),
    }))
  }, [addedProducts])

  const uploadPendingFiles = useCallback(async (): Promise<number[]> => {
    if (selectedFiles.length === 0) return []
    const uploadedIds = await Promise.all(selectedFiles.map((file) => uploadFileToGeneralFiles(file)))
    setSelectedFiles([])
    return uploadedIds
  }, [selectedFiles])

  const buildPayload = useCallback(async () => {
    const newFileIds = await uploadPendingFiles()
    const keptExistingIds = existingFileIds.filter((id) => !removedFileIds.includes(id))
    const allFileIds = [...keptExistingIds, ...newFileIds]

    const grouped = new Map<string, ProductApiData>()
    addedProducts.forEach((p) => {
      const id = p.fashionProduct?.id || p.fashionProductId
      if (id === undefined) return
      const kitKey = p.kitIndex !== null && p.kitIndex !== undefined ? String(p.kitIndex) : 'null'
      const key = `${id}-${kitKey}`
      if (grouped.has(key)) {
        const ex = grouped.get(key)!
        ex.items = [...ex.items, ...p.items]
        ex.totalItems += p.totalItems || 0
        ex.subtotal += p.subtotal || 0
      } else {
        grouped.set(key, { ...p })
      }
    })

    const toDate = (d: string) => d ? `${d}T00:00:00.000Z` : null
    const startDate = toDate(formState.startDateOfDelivery)
    const finalDate = toDate(formState.finalDateOfDelivery)

    const payload: any = {
      clientId: formState.company.id,
      supplierId: formState.supplier.id,
      sellerId: formState.seller.id,
      orderType: formState.orderType,
      paymentConditionId: formState.paymentCondition?.id || null,
      shippingTypeId: formState.shippingType?.id || null,
      trackingCode: formState.trackingCode,
      shippingCost: centsDigitsToReais(formState.shippingCost),
      totalCost: cleanCurrency(formState.totalCost),
      startDateOfDelivery: startDate,
      finalDateOfDelivery: finalDate,
      orderDate: startDate || new Date().toISOString(),
      extraInfo: formState.extraInformation,
      quantityItems: formState.quantityItems,
      quantityPieces: formState.quantityPieces,
      deliveryTime: formState.deliveryTime,
      maximumBillingAmount: formState.maximumBillingAmount,
      saleStatus: 'Em andamento',
      fileIds: allFileIds,
      items: Array.from(grouped.values()).map((p) => ({
        itemType: 'PRODUCT',
        productId: p.fashionProduct?.id || p.fashionProductId,
        quantity: p.totalItems || 0,
        descount: p.discount || 0,
        priceKg: p.subtotal || 0,
        kitIndex: p.kitIndex !== undefined ? p.kitIndex : null,
        extraInfo: JSON.stringify({
          fashionItems: p.items || [],
          colorCount: p.colorCount || 0,
          colors: p.colors || [],
          selectedColors: p.selectedColors || [],
          fashionProductName: p.fashionProduct?.name || p.name,
          fashionProductPrice: p.fashionProduct?.price || p.price,
          groupId: p.fashionProduct?.group?.id || p.group?.id,
          groupName: p.fashionProduct?.group?.name || p.group?.name,
          familyId: p.fashionProduct?.group?.family?.id || p.family?.id,
          familyName: p.fashionProduct?.group?.family?.name || p.family?.name,
          collectionId: p.fashionProduct?.group?.family?.collectionId,
        }),
      })),
    }

    return payload
  }, [formState, addedProducts, uploadPendingFiles, existingFileIds, removedFileIds])

  const withSaving = useCallback(
    async (fn: () => Promise<void>) => {
      outletContext?.setIsSaving?.(true)
      try { await fn() } finally { outletContext?.setIsSaving?.(false) }
    },
    [outletContext]
  )

  const handleSaveOrder = useCallback(async () => {
    try {
      await withSaving(async () => {
        const payload = await buildPayload()
        await axios.post(`${ORDERS_URL}/order`, payload)
        setSelectedFiles([])
        setFeedback({ isOpen: true, message: 'Pedido criado com sucesso!', statusOk: true })
      })
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: `Erro ao salvar pedido: ${err.response?.data?.message || err.message}`,
        statusOk: false,
      })
    }
  }, [buildPayload, setFeedback, withSaving])

  const handleEditSaveOrder = useCallback(async () => {
    try {
      await withSaving(async () => {
        const payload = await buildPayload()
        await axios.patch(`${ORDERS_URL}/order/${orderId}`, payload)
        setSelectedFiles([])
        setFeedback({ isOpen: true, message: 'Pedido atualizado com sucesso!', statusOk: true })
      })
    } catch {
      setFeedback({ isOpen: true, message: 'Erro ao atualizar o pedido', statusOk: false })
    }
  }, [buildPayload, orderId, setFeedback, withSaving])

  useEffect(() => {
    if (!isEditing && outletContext?.handleSave)
      outletContext.handleSave.current = handleSaveOrder
  }, [handleSaveOrder, outletContext, isEditing])

  useEffect(() => {
    if (isEditing && outletContext?.handleEditSave)
      outletContext.handleEditSave.current = handleEditSaveOrder
  }, [handleEditSaveOrder, outletContext, isEditing])

  const closeProductEdit = () => {
    if (typeof showAddProductForSection === 'number') {
      const currentKitIdx = showAddProductForSection;

      const hasProductsInKit = addedProducts.some(
        (p) => p.kitIndex === currentKitIdx
      );

      if (!hasProductsInKit) {
        setKitCount((prev) => Math.max(0, prev - 1));
      }
    }


    setEditingProductIndex(null);
    setProductToEdit(null);
    setOpenProductIndex(null);
    setShowAddProductForSection('standalone');
  };

  const handleRemoveProduct = (index: number) => {
    setAddedProducts((prev) => prev.filter((_, i) => i !== index))
    closeProductEdit()
  }

  const handleProductUpdated = (_: ProductApiData, updated: ProductApiData) => {
    const idx = editingProductIndex ?? openProductIndex
    if (idx === null) return
    setAddedProducts((prev) => {
      const next = [...prev]
      next[idx] = normalizeProductData({ ...updated, kitIndex: prev[idx].kitIndex })
      return next
    })
    closeProductEdit()
  }

  const handleNewProductAdded = (newProduct: ProductApiData) => {
    if (newProduct.isKitIntent) {
      if (!newProduct.fashionProductId) {
        if (showAddProductForSection === 'standalone') {
          const nextKitIdx = kitCount;
          setKitCount(prev => prev + 1);
          setShowAddProductForSection(nextKitIdx);
        }
        else {
          setShowAddProductForSection('standalone');
        }
        return;
      }


      if (showAddProductForSection === 'standalone') {
        const newIdx = kitCount;
        setKitCount((prev) => prev + 1);

        setAddedProducts((prev) => [
          ...prev,
          normalizeProductData({ ...newProduct, kitIndex: newIdx })
        ]);

        setShowAddProductForSection(newIdx);
        return;
      }

      const targetKitIdx = typeof showAddProductForSection === 'number'
        ? showAddProductForSection
        : kitCount - 1;

      setAddedProducts((prev) => [
        ...prev,
        normalizeProductData({ ...newProduct, kitIndex: targetKitIdx })
      ]);

      setShowAddProductForSection(targetKitIdx);
    } else {
      setAddedProducts((prev) => [
        ...prev,
        normalizeProductData({ ...newProduct, kitIndex: null })
      ]);
      setShowAddProductForSection('standalone');
    }
  };

  const handleRemoveKit = (kitIdx: number) => {
    setAddedProducts((prev) =>
      prev
        .filter((p) => p.kitIndex !== kitIdx)
        .map((p) => {
          if (p.kitIndex !== null && p.kitIndex !== undefined && p.kitIndex > kitIdx)
            return { ...p, kitIndex: p.kitIndex - 1 }
          return p
        })
    )
    setKitCount((prev) => prev - 1)
    closeProductEdit()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((f) => {
      if (f.size > FILE_SIZE_LIMIT) { alert(`Arquivo ${f.name} excede 200MB.`); return false }
      return true
    })
    setSelectedFiles((prev) => [...prev, ...files])
    e.target.value = ''
  }

  const handleRemoveFile = (i: number) =>
    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== i))

  const handlePreviewFile = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }, [])

  const handleDownloadFile = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    void downloadRemoteFile(url, file.name).finally(() => URL.revokeObjectURL(url))
  }, [])

  const handleRemoveExistingAttachment = (id: number) => {
    if (window.confirm('Tem certeza que deseja remover este anexo?')) {
      setExistingAttachments((prev) => prev.filter((a) => a.id !== id))
      setRemovedFileIds((prev) => [...prev, id])
    }
  }

  const handleViewAttachment = (a: ExistingAttachment) => {
    if (a.filePath) window.open(a.filePath, '_blank')
  }

  const handleDownloadAttachment = (a: ExistingAttachment) => {
    if (!a.filePath) return
    void downloadRemoteFile(a.filePath, a.originalName)
  }

  const renderInfoPanel = (
    isOpen: boolean,
    toggle: () => void,
    fields: { label: string; value: string }[]
  ) => (
    <InfoLink>
      <Info size={14} /> 
      <InfoPopoverWrapper>
        <span onClick={toggle}>Mais informações</span>
        {isOpen && (
          <MoreInformation>
            {fields.map(({ label, value }) => (
              <InfoName key={label}>{label}: <InfoValue>{value}</InfoValue></InfoName>
            ))}
          </MoreInformation>
        )}
      </InfoPopoverWrapper>
    </InfoLink>
  )

  const renderOptionSelect = (
    label: string,
    field: string,
    options: string[]
  ) => (
    <FormGroup>
      <Label>{label}</Label>
      <Select value={(formState as any)[field]} onChange={(e) => setField(field, e.target.value)}>
        <option value=''>Selecione</option>
        {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </Select>
    </FormGroup>
  )

  const renderProductCard = (product: ProductApiData, index: number) => {
    const totalPieces = product.sizeQuantities
      ? Object.values(product.sizeQuantities).reduce((s, q) => s + q, 0)
      : product.totalItems ?? 0

    const clampedDiscount = Math.min(100, Math.max(0, product.discount ?? 0))
    const discountDisplay = (): string => {
      if (!clampedDiscount || clampedDiscount <= 0) return '-'
      if (product.discountValue !== undefined) return formatCurrency(product.discountValue)
      const original = (product.subtotal || 0) / (1 - clampedDiscount / 100)
      return formatCurrency((original * clampedDiscount) / 100)
    }

    const fields = [
      { label: 'Produto', value: product.fashionProduct?.name ?? '-' },
      { label: 'Família', value: product.fashionProduct?.group?.family?.name || product.family?.name || '-' },
      { label: 'Cores', value: countColors(product) },
      {
        label: 'Precificação',
        value: typeof product.fashionProduct?.price === 'number'
          ? formatCurrency(product.fashionProduct.price) : '-',
      },
      { label: 'Quantidade de peças', value: totalPieces },
      { label: 'Desconto', value: discountDisplay() },
      {
        label: 'Subtotal',
        value: typeof product.subtotal === 'number' ? formatCurrency(product.subtotal) : '-',
      },
    ]


    return (
      <Card key={`${product.fashionProductId}-${index}`}>
        <ProductShort>
          <ProductDiv>
            <ProductFieldsRow>
              {fields.map(({ label, value }, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <SeparatorImg src={rectangle} alt='' />}
                  <ShortLabel>
                    <ShortLabelText>{label}:</ShortLabelText>&nbsp;
                    {getSafeValue(value)}
                  </ShortLabel>
                </React.Fragment>
              ))}
            </ProductFieldsRow>
            <ActionsDiv>
              <ActionIconImg src={trash} alt='Remover'
                onClick={() => handleRemoveProduct(index)} />
              <ActionIconImg src={editPen} alt='Editar'
                onClick={() => {
                  if (isEditing) {
                    setOpenProductIndex(openProductIndex === index ? null : index)
                  } else {
                    setProductToEdit(product)
                    setEditingProductIndex(index)
                    const section = product.kitIndex !== null && product.kitIndex !== undefined
                      ? product.kitIndex
                      : 'standalone'
                    setShowAddProductForSection(section)
                  }
                }} />
            </ActionsDiv>
          </ProductDiv>
        </ProductShort>

        {isEditing && openProductIndex === index && (
          <AddProductForm
            order={addedProducts}
            setOrder={setAddedProducts}
            product={product}
            onClose={() => setOpenProductIndex(null)}
            onProductUpdated={handleProductUpdated}
            onRemoveProduct={handleRemoveProduct}
            isEditing={true}
            onProductAdded={handleNewProductAdded}
            isKitMode={isKitMode}
            setIsKitMode={setIsKitMode}
          />
        )}
      </Card>
    )
  }

  const renderSectionAddProduct = (section: 'standalone' | number) => {
    const isThisSectionOpen = showAddProductForSection === section
    const isEditing_ = editingProductIndex !== null

    return (
      <AddProductContainer>
        {isThisSectionOpen && (
          <AddProductForm
            order={addedProducts}
            setOrder={setAddedProducts}
            product={productToEdit}
            isEditing={isEditing_}
            onProductAdded={!isEditing_ ? handleNewProductAdded : undefined}
            onProductUpdated={isEditing_ ? handleProductUpdated : undefined}
            onRemoveProduct={handleRemoveProduct}
            onClose={closeProductEdit}
            isKitMode={isKitMode}
            setIsKitMode={setIsKitMode}
          />
        )}
        {/* <AddButton onClick={() => {
          setProductToEdit(null)
          setEditingProductIndex(null)
          setOpenProductIndex(null)
          setShowAddProductForSection(section)
        }}>
          <AddButtonImg src={plus} />
          <AddButtonText>Adicionar produto</AddButtonText>
        </AddButton> */}
      </AddProductContainer>
    )
  }

  if (loading) return <div>Carregando...</div>

  const standaloneProducts = addedProducts
    .map((p, i) => ({ product: p, index: i }))
    .filter(({ product }) => product.kitIndex === null || product.kitIndex === undefined)

  const kitProducts = (kitIdx: number) =>
    addedProducts
      .map((p, i) => ({ product: p, index: i }))
      .filter(({ product }) => product.kitIndex === kitIdx)

  return (
    <>
      <Card>
        <FormGrid>
          <FormGroup>
            <CompanySelect
              label='Meu cnpj'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
              displayField='companyName'
              queryParams={{ isMain: 'true' }}
              value={formState.supplier}
              onChange={(v) => setField('supplier', v)}
            />
            {renderInfoPanel(
              isInformationOpen && formState.supplier.id > 0,
              () => setIsInformationOpen((v) => !v),
              [
                { label: 'Razão Social', value: (formState.supplier as any).companyName || 'N/A' },
                { label: 'CNPJ/CPF', value: (formState.supplier as any).cnpj || (formState.supplier as any).cpf || 'N/A' },
                { label: 'Cidade/UF', value: `${(formState.supplier as any).city?.name || 'N/A'}/${(formState.supplier as any).state?.name || 'N/A'}` },
                { label: 'Telefone', value: (formState.supplier as any).contacts?.[0]?.phoneNumber || 'N/A' },
                { label: 'Email', value: (formState.supplier as any).contacts?.[0]?.email || 'N/A' },
              ]
            )}
          </FormGroup>

          <FormGroup>
            <VendorSelect
              label='Vendedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll?filters=${encodeURIComponent(JSON.stringify([{ field: 'Perfil', type: 'Contém', text: 'VENDEDOR' }]))}`}
              displayField='name'
              value={formState.seller}
              onChange={(v) => setField('seller', v)}
            />
            {renderInfoPanel(
              isSellerInfoOpen && formState.seller.id > 0,
              () => setIsSellerInfoOpen((v) => !v),
              [
                { label: 'Nome', value: (formState.seller as any).name || 'N/A' },
                { label: 'Email', value: (formState.seller as any).email || 'N/A' },
                { label: 'Perfil', value: (formState.seller as any).profile?.name || 'N/A' },
              ]
            )}
          </FormGroup>

          <FormGroup>
            <CompanySelect
              label='Cliente'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
              displayField='companyName'
              queryParams={{ companyType: 'Cliente', excludeMainCompany: 'true' }}
              value={formState.company}
              onChange={(v) => setField('company', v)}
            />
            {renderInfoPanel(
              isClientInformationOpen && formState.company.id > 0,
              () => setIsClientInformationOpen((v) => !v),
              [
                { label: 'Razão Social', value: (formState.company as any).companyName || 'N/A' },
                { label: 'CNPJ/CPF', value: (formState.company as any).cnpj || (formState.company as any).cpf || 'N/A' },
                { label: 'Cidade/UF', value: `${(formState.company as any).city?.name || 'N/A'}/${(formState.company as any).state?.name || 'N/A'}` },
                { label: 'Telefone', value: (formState.company as any).contacts?.[0]?.phoneNumber || 'N/A' },
                { label: 'Email', value: (formState.company as any).contacts?.[0]?.email || 'N/A' },
              ]
            )}
          </FormGroup>
        </FormGrid>

        <Separator />

        <CollapsibleHeader onClick={() => setIsDetailsOpen((v) => !v)}>
          <Title>Detalhes do pedido</Title>
          <Button>{isDetailsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</Button>
        </CollapsibleHeader>

        {isDetailsOpen && (
          <>
            <FormGrid className="mt">
              <FormGroup>
                <Label>Tipo de pedido</Label>
                <Select value={formState.orderType} onChange={(e) => setField('orderType', e.target.value)}>
                  <option value=''>Selecione</option>
                  <option value='Venda'>Venda</option>
                  <option value='Reposição'>Reposição</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Data início da entrega</Label>
                <InputText type='date' max='9999-12-31' value={formState.startDateOfDelivery}
                  onChange={(e) => setField('startDateOfDelivery', e.target.value)} />
              </FormGroup>
              <FormGroup>
                <Label>Data final da entrega</Label>
                <InputText type='date' max='9999-12-31' value={formState.finalDateOfDelivery}
                  onChange={(e) => setField('finalDateOfDelivery', e.target.value)} />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <InfiniteSelect
                  label="Condição de pagamento"
                  placeholder="Selecione a condição"
                  mode="crud"
                  endpointConfig={{
                    baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/select-in-order`,
                    listPath: '/payment-conditions',
                    createPath: '/payment-conditions',
                    deletePath: '/payment-conditions',
                  }}
                  value={formState.paymentCondition as any}
                  onSelect={(val) => setField('paymentCondition', val)}
                />
              </FormGroup>

              <FormGroup>
                <InfiniteSelect
                  label="Tipo de frete"
                  placeholder="Selecione o tipo de frete"
                  mode="crud"
                  endpointConfig={{
                    baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/select-in-order`,
                    listPath: '/shipping-types',
                    createPath: '/shipping-types',
                    deletePath: '/shipping-types',
                  }}
                  value={formState.shippingType as any}
                  onSelect={(val) => setField('shippingType', val)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Valor do frete</Label>
                <InputText type='text' placeholder='Digite o valor do frete'
                  value={formatCentDigitsForInput(formState.shippingCost)}
                  onChange={(e) => setField('shippingCost', e.target.value.replace(/\D/g, '').slice(0, 12))} />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Código de rastreio</Label>
                <InputText type='text' placeholder='Digite o código de rastreio'
                  value={formState.trackingCode}
                  onChange={(e) => setField('trackingCode', e.target.value)} />
              </FormGroup>

              <FormGroup>
                <Label>Anexos</Label>
                <AttachmentManager
                  selectedFiles={selectedFiles}
                  existingAttachments={existingAttachments}
                  onFileChange={handleFileChange}
                  onRemoveFile={handleRemoveFile}
                  onViewAttachment={handleViewAttachment}
                  onDownloadAttachment={handleDownloadAttachment}
                  onRemoveExistingAttachment={isEditing ? handleRemoveExistingAttachment : undefined}
                  formatFileSize={formatFileSize}
                  onPreviewFile={handlePreviewFile}
                  onDownloadFile={handleDownloadFile}
                />
              </FormGroup>

              <FormGroup>
                <Label>Valor máximo de faturamento</Label>
                <InputText type='text' placeholder='Digite o valor máximo'
                  value={formatCentDigitsForInput(formState.maximumBillingAmount)}
                  onChange={(e) => setField('maximumBillingAmount', e.target.value.replace(/\D/g, '').slice(0, 12))} />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Informações adicionais</Label>
                <InputText type='text' value={formState.extraInformation}
                  onChange={(e) => setField('extraInformation', e.target.value)} />
              </FormGroup>
            </FormGrid>
          </>
        )}

        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel>Quantidade de conjuntos:</SummaryLabel>
            <SummaryValue>{kitCount}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Quantidade de itens:</SummaryLabel>
            <SummaryValue>{formState.quantityItems}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Quantidade de peças:</SummaryLabel>
            <SummaryValue>{formState.quantityPieces}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total:</SummaryLabel>
            <SummaryValue>{formState.totalCost}</SummaryValue>
          </SummaryItem>
        </Summary>

      </Card>

      {showAddProductForSection === 'standalone' && (
        <AddProductContainer>
          <AddProductForm
            order={addedProducts}
            setOrder={setAddedProducts}
            product={productToEdit}
            isEditing={editingProductIndex !== null}
            onProductAdded={editingProductIndex === null ? handleNewProductAdded : undefined}
            onProductUpdated={editingProductIndex !== null ? handleProductUpdated : undefined}
            onRemoveProduct={handleRemoveProduct}
            onClose={closeProductEdit}
            isKitMode={isKitMode}
            setIsKitMode={setIsKitMode}
          />
        </AddProductContainer>
      )}

      {standaloneProducts.map(({ product, index }) => renderProductCard(product, index))}


      <ActionButtonsRow>
        {/* <AddButton onClick={() => {
          setProductToEdit(null)
          setEditingProductIndex(null)
          setOpenProductIndex(null)
          setShowAddProductForSection('standalone')
        }}>
          <AddButtonImg src={plus} />
          <AddButtonText>Adicionar produto</AddButtonText>
        </AddButton> */}

        {/* <AddButton onClick={() => {
          setKitCount((prev) => prev + 1)
          closeProductEdit()
        }}>
          <AddButtonImg src={plus} />
          <AddButtonText>Adicionar conjunto</AddButtonText>
        </AddButton> */}
      </ActionButtonsRow>

      {Array.from({ length: kitCount }, (_, kitIdx) => {
        const kits = kitProducts(kitIdx)

        const kitTotalItems = kits.length
        const kitTotalPieces = kits.reduce((sum, { product }) => {
          const pieces = product.sizeQuantities
            ? Object.values(product.sizeQuantities).reduce((s, q) => s + q, 0)
            : product.totalItems || 0
          return sum + pieces
        }, 0)
        const kitTotal = kits.reduce((sum, { product }) => sum + (product.subtotal || 0), 0)

        return (
          <React.Fragment key={`kit-section-${kitIdx}`}>

            <Card>
              <KitSummaryRow>
                <Summary>
                  <SummaryItem>
                    <KitSummaryLabel>Conjunto {kitIdx + 1}</KitSummaryLabel>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Quantidade de itens:</SummaryLabel>
                    <SummaryValue>{kitTotalItems}</SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Quantidade de peças:</SummaryLabel>
                    <SummaryValue>{kitTotalPieces}</SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Total:</SummaryLabel>
                    <SummaryValue>{formatCurrency(kitTotal)}</SummaryValue>
                  </SummaryItem>
                </Summary>
                <ActionsDiv>
                  <KitTrashImg
                    src={trash}
                    alt='Remover conjunto'
                    onClick={() => handleRemoveKit(kitIdx)}
                  />
                  <ActionIconImg
                    src={editPen}
                    alt="Editar conjunto"
                    onClick={() => {
                      setProductToEdit(null);
                      setEditingProductIndex(null);
                      setOpenProductIndex(null);
                      setShowAddProductForSection(kitIdx);
                      setIsKitMode(true);
                    }}
                  />
                </ActionsDiv>
              </KitSummaryRow>
            </Card>

            {kits.map(({ product, index }) => renderProductCard(product, index))}

            {renderSectionAddProduct(kitIdx)}
          </React.Fragment>
        )
      })}
    </>
  )
}

export default RegisterFashionOrder